import { useEffect, useState } from "react";
import { Title2, Body1, Spinner, makeStyles, tokens } from "@fluentui/react-components";

interface WhoamiResponse {
  userDetails: string;
  userId: string;
  userRoles: string[];
  groupIds: string[];
  groupSource: "claims" | "graph" | "none";
}

const useStyles = makeStyles({
  panel: {
    marginTop: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: tokens.fontFamilyMonospace,
    whiteSpace: "pre-wrap"
  },
  hint: {
    marginTop: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3
  }
});

/**
 * Proof-of-life page. Calls /api/whoami and renders the SWA principal + the
 * resolved Entra group IDs. Used to demo end-to-end Entra + SWA + Functions
 * plumbing before any AWS or dashboard logic is layered on.
 */
export default function Whoami() {
  const styles = useStyles();
  const [data, setData] = useState<WhoamiResponse | "loading" | "error">("loading");

  useEffect(() => {
    fetch("/api/whoami")
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as WhoamiResponse;
      })
      .then((d) => setData(d))
      .catch(() => setData("error"));
  }, []);

  if (data === "loading") return <Spinner size="medium" label="Loading…" />;
  if (data === "error") return <Body1>Couldn't load /api/whoami. Check the Functions logs.</Body1>;

  return (
    <div>
      <Title2>Hello, {data.userDetails}</Title2>
      <Body1>
        Your Entra group IDs ({data.groupSource === "graph" ? "via Microsoft Graph fallback" : data.groupSource === "claims" ? "from token claims" : "none found"}):
      </Body1>
      <pre className={styles.panel}>{JSON.stringify(data, null, 2)}</pre>
      <div className={styles.hint}>
        This page proves the Entra → SWA → Functions plumbing end-to-end. If you can
        see your email and group IDs above, the platform is healthy.
      </div>
    </div>
  );
}
